import type { FetchQueryOptions, QueryClient, QueryKey } from '@tanstack/react-query';

// A route loader's read of the entity its page renders: the cached value when
// there is one, else one fetch into the same cache entry the page's own query
// reads, so the page never fetches it twice.
//
// `prefetchQuery` stores a failure in the cache instead of throwing, which is
// what a detail page wants: a 404 has to reach the page's branded
// NotFoundPage and anything else its retry surface, not the error boundary a
// thrown loader error lands in. The page's query must set
// `retryOnMount: false`, or it refetches the failure the loader left behind.
//
// A hover preload runs the loader too, and its failure is left in the cache
// the same way. The router reruns the loader on the click
// (`defaultPreloadStaleTime` in app.tsx), so the click refetches a failed
// preload rather than render it.
//
// Any cached value is returned as it is, stale or invalidated; the page's
// query revalidates it. A route whose tab title is editable copy reads with
// `loadCurrentForRoute` instead.
export async function loadForRoute<TData, TError, TKey extends QueryKey>(
  queryClient: QueryClient,
  options: FetchQueryOptions<TData, TError, TData, TKey>,
): Promise<TData | undefined> {
  const cached = queryClient.getQueryData<TData>(options.queryKey);
  if (cached !== undefined) return cached;
  return fetchForRoute(queryClient, options);
}

// `loadForRoute` for a route whose tab is titled from copy an editor can
// change in this session (resource and learning track search titles). Those
// routes block their navigation on a stale reload (`EDITABLE_TITLE_RELOAD`),
// so the reload's result is what the tab is titled from (`subscribePageViews`). A value
// that is merely stale by time is still returned as it is. An invalidated one
// is not: the write that invalidated it is saying the cached copy is wrong,
// so the loader waits on the refetch rather than title the page from the copy
// the write replaced. The refetch lands in the shared entry, so the page's
// query mounts on it and does not fetch again; one already in flight is
// joined, not repeated.
//
// A first visit keeps the query's retries, behind the route's pending
// component. A revisit (the entry exists: it failed, or a write invalidated
// it) tries once: the navigation is blocked on this read while the previous
// page stays on screen, so retries with backoff would hold it for seconds on
// an API that is down. The page's own query still offers its retry surface.
export async function loadCurrentForRoute<TData, TError, TKey extends QueryKey>(
  queryClient: QueryClient,
  options: FetchQueryOptions<TData, TError, TData, TKey>,
): Promise<TData | undefined> {
  const cached = queryClient.getQueryState<TData, TError>(options.queryKey);
  if (cached === undefined) return fetchForRoute(queryClient, options);
  if (cached.data !== undefined && !cached.isInvalidated) return cached.data;
  return fetchForRoute(queryClient, { ...options, retry: false });
}

// When the fetch fails, nothing is returned, even if an older value is still
// in the entry: the page renders that failure, and the tab falls back to the
// section title rather than name the entity the failure is about.
async function fetchForRoute<TData, TError, TKey extends QueryKey>(
  queryClient: QueryClient,
  options: FetchQueryOptions<TData, TError, TData, TKey>,
): Promise<TData | undefined> {
  await queryClient.prefetchQuery(options);
  const settled = queryClient.getQueryState<TData, TError>(options.queryKey);
  return settled?.status === 'success' ? settled.data : undefined;
}
